import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMERGENT_AUTH_URL = "https://auth.emergentagent.com/";

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(res.data.user);
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      const hash = window.location.hash || "";
      // Emergent redirects back with #session_id=xxx
      if (hash.includes("session_id=")) {
        const sessionId = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
        if (sessionId) {
          try {
            const res = await axios.post(
              `${API}/auth/session`,
              {},
              { headers: { "X-Session-Id": sessionId }, withCredentials: true }
            );
            setUser(res.data.user);
          } catch {
            await fetchMe();
          } finally {
            // Clean the session_id out of the URL
            window.history.replaceState(null, "", window.location.pathname + window.location.search);
          }
          setLoading(false);
          return;
        }
      }
      await fetchMe();
      setLoading(false);
    };
    init();
  }, [fetchMe]);

  const login = useCallback(() => {
    const redirect = `${window.location.origin}/`;
    window.location.href = `${EMERGENT_AUTH_URL}?redirect=${encodeURIComponent(redirect)}`;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {
      /* ignore */
    }
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
